import { useEffect, useState } from 'react';

import { PaginatorAsync } from '@/components/PaginatorAsync';
import { PropsWithClassName } from '@/types';

type Props<T, K> = PropsWithClassName<{
  itemsPerPage?: number;
  items: Array<T | K>;
  currentContentPage: (obj: Array<T | K>) => void;
  currentNumberPage?: (page: number) => void;
  loadMore?: (pageNumber?: number) => void;
  totalItems?: number;
  marginPagesDisplayed?: number;
  breakLabel?: string | React.ReactNode;
  pageRangeDisplayed?: number;
  usePageLabelBuilder?: boolean;
}>;

export const Paginator = <T, K>({
  itemsPerPage = 10,
  items,
  currentContentPage,
  currentNumberPage,
  totalItems,
  className,
  marginPagesDisplayed = 0,
  breakLabel = '',
  pageRangeDisplayed = 5,
  usePageLabelBuilder = false,
}: Props<T, K>) => {
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    const totalPages = totalItems
      ? Math.ceil(totalItems / itemsPerPage)
      : Math.ceil(items.length / itemsPerPage);
    setPageCount(totalPages);
    const startOffset = currentPage * itemsPerPage;
    const endOffset = startOffset + itemsPerPage;
    currentContentPage(items.slice(startOffset, endOffset));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, items, totalItems]);

  const goToPage = (page: number) => {
    setCurrentPage(page);
    currentNumberPage && currentNumberPage(page);
  };

  return (
    <PaginatorAsync
      breakLabel={breakLabel}
      className={className}
      currentPage={currentPage}
      marginPagesDisplayed={marginPagesDisplayed}
      pageCount={pageCount}
      pageRangeDisplayed={pageRangeDisplayed}
      usePageLabelBuilder={usePageLabelBuilder}
      onPageChange={({ selected }) => goToPage(selected)}
    />
  );
};
